import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { DEFAULT_DOWNLOAD_FILENAME, DEFAULT_INITIAL_PAGE } from './constants';
import { PdfPreviewSearchBar } from './PdfPreviewSearchBar';
import { PdfPreviewToolbar } from './PdfPreviewToolbar';
import { ThumbnailRail } from './ThumbnailRail';
import type { PdfDocumentSource, PdfPageLayer } from './types';
import { usePdfDocumentPreview } from './usePdfDocumentPreview';

import styles from './PdfDocumentPreview.module.scss';

export type PdfDocumentPreviewProps = {
  source: PdfDocumentSource | null;
  initialPage?: number;
  markedPages?: readonly number[];
  pageLayers?: readonly PdfPageLayer[];
  showToolbar?: boolean;
  showThumbnails?: boolean;
  onLoad?: (pageCount: number) => void;
  onError?: (error: Error) => void;
  onPageChange?: (pageNumber: number) => void;
  className?: string;
  style?: CSSProperties;
  filename?: string;
  ariaLabel?: string;
};

export function PdfDocumentPreview({
  source,
  initialPage = DEFAULT_INITIAL_PAGE,
  markedPages = [],
  pageLayers = [],
  showToolbar = true,
  showThumbnails = true,
  onLoad,
  onError,
  onPageChange,
  className,
  style,
  filename = DEFAULT_DOWNLOAD_FILENAME,
  ariaLabel,
}: PdfDocumentPreviewProps) {
  const { t } = useTranslation();
  const preview = usePdfDocumentPreview({
    source,
    initialPage,
    markedPages,
    pageLayers,
    showThumbnails,
    filename,
    onLoad,
    onError,
    onPageChange,
  });

  return (
    <section
      aria-label={ariaLabel ?? t('app.pdfDocumentPreview.title')}
      className={clsx(styles['root'], className)}
      data-testid="pdf-document-preview"
      style={style}
    >
      {showToolbar ? (
        <PdfPreviewToolbar
          currentPage={preview.currentPage}
          isHandTool={preview.isHandTool}
          isHorizontal={preview.isHorizontal}
          isPagesOpen={preview.isPagesOpen}
          numPages={preview.numPages}
          onDownload={preview.download}
          onFit={preview.setFit}
          onOpenSearch={preview.openSearch}
          onPageCommit={preview.jumpToPage}
          onPrint={preview.print}
          onRotate={preview.rotate}
          onToggleHandTool={preview.toggleHandTool}
          onTogglePages={preview.togglePages}
          onToggleScrollMode={preview.toggleScrollMode}
          onToggleSearch={preview.toggleSearch}
          onZoom={preview.zoom}
          scalePct={preview.scalePct}
        />
      ) : null}
      {preview.isSearchOpen ? (
        <PdfPreviewSearchBar
          matches={preview.matches}
          onClose={preview.closeSearch}
          onFind={preview.runFind}
          onQueryChange={preview.setQuery}
          query={preview.query}
        />
      ) : null}
      <div className={styles['body']}>
        {preview.isPagesOpen ? (
          <ThumbnailRail
            currentPage={preview.currentPage}
            markedPages={preview.markedPageSet}
            numPages={preview.numPages}
            onSelect={preview.jumpToPage}
            pdf={preview.pdf}
            status={preview.status}
          />
        ) : null}
        <div className={styles['viewer-host']}>
          <div
            className={clsx(styles['viewer-container'], {
              [styles['viewer-container--hand']]: preview.isHandTool,
              [styles['viewer-container--grabbing']]: preview.isPanning,
            })}
            data-testid="pdf-document-preview-viewer-container"
            ref={preview.containerRef}
            tabIndex={0}
          >
            <div
              className="pdfViewer"
              data-testid="pdf-document-preview-viewer"
              ref={preview.viewerElementRef}
            />
          </div>
          {preview.status === 'loading' ? (
            <div className={styles['status']}>
              <LoadingIndicator
                data-testid="pdf-document-preview-loading"
                size="2em"
              />
              <span>{t('app.pdfDocumentPreview.loading')}</span>
            </div>
          ) : null}
          {preview.status === 'error' ? (
            <div className={styles['status']}>
              {t('app.pdfDocumentPreview.loadingError')}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
